import { clsx } from 'clsx';
import { useMasterDocumentTranslation } from './i18n';
import type { FactBoxCfdeProps, CfdeStatus } from './MasterDocument.types';

/**
 * Configuración de colores por estado DIAN.
 *
 * Se utilizan colores semánticos de Tailwind (amber, blue, green, red) porque
 * el sistema de tokens actual solo define `error-*`. Cuando se agreguen tokens
 * `success-*`, `warning-*` e `info-*` al sistema, migrar a esos tokens.
 */
const statusConfig: Record<CfdeStatus, { translationKey: string; bg: string; color: string; icon: string }> = {
  PENDING:  { translationKey: 'master_document.cfde.status.pending',  bg: 'bg-amber-100 dark:bg-amber-900/20', color: 'text-amber-700 dark:text-amber-400', icon: '⏳' },
  SENT:     { translationKey: 'master_document.cfde.status.sent',     bg: 'bg-blue-100 dark:bg-blue-900/20',   color: 'text-blue-700 dark:text-blue-400',   icon: '📤' },
  ACCEPTED: { translationKey: 'master_document.cfde.status.accepted', bg: 'bg-green-100 dark:bg-green-900/20', color: 'text-green-700 dark:text-green-400', icon: '✓' },
  REJECTED: { translationKey: 'master_document.cfde.status.rejected', bg: 'bg-error-bg dark:bg-error-bg/20',   color: 'text-error-content dark:text-error-content', icon: '✗' },
  ERROR:    { translationKey: 'master_document.cfde.status.error',    bg: 'bg-error-bg dark:bg-error-bg/20',   color: 'text-error-content dark:text-error-content', icon: '!' },
};

/**
 * Widget de información DIAN (Factura Electrónica) para el panel FactBox.
 *
 * Muestra el estado de transmisión DIAN con su insignia de color, el número de
 * FE, CUFE, fecha de emisión, contador de impresiones y enlace al QR. Expone
 * acciones de Retransmitir (estados REJECTED/ERROR) y Descargar PDF (estado
 * ACCEPTED) cuando se proveen los callbacks correspondientes.
 *
 * @see docs/colors.md - Sistema de tokens de color
 * @see MasterDocument.types - FactBoxCfdeProps, CfdeStatus
 *
 * @example
 * ```tsx
 * <FactBoxCfde
 *   data={{ status: 'ACCEPTED', number: 'FE-0001', cufe: 'abc123' }}
 *   onDownloadPdf={() => downloadPdf()}
 * />
 * ```
 */
export function FactBoxCfde({ data, onRetransmit, onDownloadPdf, className }: FactBoxCfdeProps) {
  const { t } = useMasterDocumentTranslation();
  const cfg = statusConfig[data.status];
  const statusLabel = t(cfg.translationKey);

  return (
    <div className={clsx('flex flex-col gap-2.5', className)}>
      {/* Status badge */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-content-tertiary dark:text-dark-content-secondary">{t('master_document.cfde.dianStatus')}</span>
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color}`}
          aria-label={t('master_document.cfde.statusAria', { status: statusLabel })}
        >
          <span className="text-[10px]" aria-hidden="true">{cfg.icon}</span>
          {statusLabel}
        </span>
      </div>

      {/* CFDE Number */}
      {data.number && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-content-tertiary dark:text-dark-content-secondary">{t('master_document.cfde.feNumber')}</span>
          <span className="text-xs font-semibold text-content-primary dark:text-dark-content-primary">
            {data.number}
          </span>
        </div>
      )}

      {/* CUFE */}
      {data.cufe && (
        <div>
          <span className="text-xs text-content-tertiary dark:text-dark-content-secondary block mb-0.5">CUFE</span>
          <span className="text-[10px] text-content-secondary dark:text-dark-content-secondary break-all leading-relaxed">
            {data.cufe}
          </span>
        </div>
      )}

      {/* Issue date */}
      {data.issueDate && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-content-tertiary dark:text-dark-content-secondary">{t('master_document.cfde.issueDate')}</span>
          <span className="text-xs text-content-primary dark:text-dark-content-primary">
            {data.issueDate}
          </span>
        </div>
      )}

      {/* Print count */}
      {data.printCount != null && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-content-tertiary dark:text-dark-content-secondary">{t('master_document.cfde.prints')}</span>
          <span className="text-xs font-semibold text-content-primary dark:text-dark-content-primary">
            {data.printCount}
          </span>
        </div>
      )}

      {/* QR link */}
      {data.qrUrl && (
        <a
          href={data.qrUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline"
        >
          {t('master_document.cfde.viewQr')}
        </a>
      )}

      {/* Actions */}
      {(onRetransmit || onDownloadPdf) && (
        <div className="flex gap-2 pt-1 border-t border-border-secondary dark:border-dark-border-primary">
          {onRetransmit && (data.status === 'REJECTED' || data.status === 'ERROR') && (
            <button
              type="button"
              onClick={onRetransmit}
              aria-label={t('master_document.cfde.retransmit')}
              className="flex-1 text-xs font-semibold text-primary-600 dark:text-primary-400 py-1.5 rounded-md hover:bg-primary-100 dark:hover:bg-primary-600/10 transition-colors"
            >
              {t('master_document.cfde.retransmitButton')}
            </button>
          )}
          {onDownloadPdf && data.status === 'ACCEPTED' && (
            <button
              type="button"
              onClick={onDownloadPdf}
              aria-label={t('master_document.cfde.downloadPdf')}
              className="flex-1 text-xs font-semibold text-primary-600 dark:text-primary-400 py-1.5 rounded-md hover:bg-primary-100 dark:hover:bg-primary-600/10 transition-colors"
            >
              {t('master_document.cfde.downloadPdfButton')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
